import { useDataSources } from '../../hooks';
import { Autocomplete, CircularProgress, Grid, InputLabel, Stack, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { DataSourceDto, GenericTableDataFileDto, GenericTableSheetDto } from '../../../../../api/generic-tables/dto';
import type { FormikErrors, FormikTouched } from 'formik';

export type DataSourceConfigProps = {
    value?: DataSourceDto | null;
    onChange: (newValue: DataSourceDto | null) => void;
    error?: FormikErrors<DataSourceDto> | string | undefined;
    touched?: FormikTouched<DataSourceDto> | boolean | undefined;
    onTouched?: () => void;
    disabled?: boolean;
};

export const DataSourceConfig = ({
    value,
    onChange,
    onTouched,
    touched = false,
    error,
    disabled = false,
}: DataSourceConfigProps) => {
    const { dataSources } = useDataSources();
    const dataSourceSheetOptions =
        dataSources && value?.fileId
            ? dataSources.find((dataSource) => dataSource.fileId === value.fileId)?.sheets ?? []
            : [];

    const fileIdTouched = Boolean(typeof touched === 'object' ? touched.fileId : touched);
    const sheetIdTouched = Boolean(typeof touched === 'object' ? touched.sheetId : touched);
    const fileIdError = typeof error === 'object' ? error.fileId : error;
    const sheetIdError = typeof error === 'object' ? error.sheetId : undefined;
    const isLoading = !dataSources || Boolean(value && !dataSources.find((ds) => ds.fileId === value.fileId));

    return (
        <>
            <Grid item xs={12} rowGap={3}>
                <Stack spacing={1}>
                    <InputLabel>
                        <FormattedMessage id="inside-fifa.generic-tables.data-source-file" />
                    </InputLabel>
                    {dataSources ? (
                        <Autocomplete<GenericTableDataFileDto>
                            loading={isLoading}
                            options={dataSources ?? []}
                            getOptionLabel={(option) => `${option.name} (id: ${option.fileId})`}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    error={fileIdTouched && Boolean(fileIdError)}
                                    helperText={fileIdTouched && fileIdError}
                                />
                            )}
                            disabled={disabled}
                            onBlur={() => onTouched?.()}
                            onChange={(_event, value) => {
                                if (value?.fileId === undefined || !dataSources) {
                                    onChange(null);
                                    return;
                                }

                                const sheetId = value.sheets[0]?.sheetId;

                                if (!sheetId) {
                                    return;
                                }
                                onChange({
                                    fileId: value.fileId,
                                    sheetId,
                                });
                            }}
                            value={
                                value
                                    ? dataSources?.find((option) => option.fileId === value.fileId) ?? null
                                    : null
                            }
                        />
                    ) : (
                        <CircularProgress color="inherit" size={32} />
                    )}
                </Stack>
            </Grid>
            <Grid item xs={12} rowGap={3}>
                <Stack spacing={1}>
                    <InputLabel>
                        <FormattedMessage id="inside-fifa.generic-tables.data-source-sheet" />
                    </InputLabel>
                    <Autocomplete<GenericTableSheetDto, false, boolean, false>
                        loading={isLoading}
                        disableClearable
                        options={dataSourceSheetOptions}
                        getOptionLabel={(option) => option?.name || ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={sheetIdTouched && Boolean(sheetIdError)}
                                helperText={sheetIdTouched && sheetIdError}
                            />
                        )}
                        disabled={disabled || !value?.sheetId}
                        onChange={(_event, sheetValue) => {
                            if (!value || !sheetValue) {
                                onChange(null);
                                return;
                            }

                            onChange({
                                ...value,
                                sheetId: sheetValue.sheetId,
                            });
                        }}
                        value={
                            (value && dataSourceSheetOptions.find((option) => option.sheetId === value.sheetId)) ?? null
                        }
                    />
                </Stack>
            </Grid>
        </>
    );
};
